/**
 * Maps API responses from create-user endpoints (e.g. POST /tannery-admin/users,
 * POST /super-admin/tannery-users) to clear, user-facing messages.
 */

const formatValidationDetail = (detailList) => {
  if (!Array.isArray(detailList)) {
    return "";
  }
  const validationMessages = detailList
    .map((detailItem) => {
      const fieldName = Array.isArray(detailItem?.loc)
        ? detailItem.loc[detailItem.loc.length - 1]
        : "";
      const prettyFieldName =
        typeof fieldName === "string"
          ? fieldName.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
          : "";
      return prettyFieldName
        ? `${prettyFieldName}: ${detailItem?.msg || "Invalid value."}`
        : detailItem?.msg || "";
    })
    .filter(Boolean);

  return validationMessages.length > 0
    ? validationMessages.join(" ")
    : "Please check the form: some fields did not pass validation.";
};

const stringDetail = (payload) => {
  if (!payload || typeof payload !== "object") {
    return "";
  }
  if (typeof payload.detail === "string") {
    return payload.detail.trim();
  }
  return "";
};

/**
 * @param {number} status - HTTP status code
 * @param {object|null} payload - Parsed JSON body, if any
 * @param {string} [fallbackMessage]
 * @returns {string}
 */
export function getCreateTanneryUserErrorMessage(
  status,
  payload,
  fallbackMessage = "Unable to create user right now."
) {
  const fallback =
    typeof fallbackMessage === "string" && fallbackMessage.trim()
      ? fallbackMessage.trim()
      : "Unable to create user right now.";
  const detail = stringDetail(payload);

  if (status === 401) {
    return "Your session is missing or invalid. Please log in again.";
  }

  if (status === 403) {
    if (detail === "Insufficient permissions.") {
      return "You do not have permission to create users. Only tannery admins and super admins can do this.";
    }
    if (detail === "Executive users can only be created by super admin.") {
      return "Executive users can only be created by a super admin. Ask a super admin to create this account.";
    }
    if (detail === "Tannery admin users can only be created by super admin.") {
      return "Tannery admin accounts can only be created by a super admin. Ask a super admin to create this account.";
    }
    if (detail) {
      return detail;
    }
    return "You do not have permission to perform this action.";
  }

  if (status === 400) {
    if (detail === "Tannery admin must belong to a tannery.") {
      return "Your tannery admin account is not linked to a tannery. Contact support before creating users.";
    }
    if (
      detail ===
      "tannery_id is required when creating users for a tannery (DataEntry, TokenGenerator, TanneryAdmin)."
    ) {
      return "Select a tannery for this role. Data entry, token generator, and tannery admin users must belong to a tannery.";
    }
    if (detail) {
      return detail;
    }
    return "The request could not be completed. Check your entries and try again.";
  }

  if (status === 404) {
    if (detail === "Tannery not found.") {
      return "The selected tannery was not found. Refresh the list and pick a valid tannery.";
    }
    if (detail) {
      return detail;
    }
    return "The requested resource was not found.";
  }

  if (status === 409) {
    if (detail === "Username already exists." || detail.toLowerCase().includes("username already")) {
      return "That username is already in use (any role or tannery). Choose a different username, or update the existing user instead of creating a new one.";
    }
    if (detail) {
      return detail;
    }
    return "This username conflicts with an existing user. Choose a different username or update the existing account.";
  }

  if (status === 422) {
    if (Array.isArray(payload?.detail)) {
      return formatValidationDetail(payload.detail);
    }
    if (detail) {
      return detail;
    }
    return "Please check the form: username, password, and other fields must meet the requirements.";
  }

  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }
  if (detail) {
    return detail;
  }

  return fallback;
}
